import type {
    FC,
    KeyboardEvent,
    PointerEvent,
    ReactNode
} from 'react'

import { useRef, useState } from 'react'
import Image from 'next/image'
import { ChevronsLeftRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { gsap, useGSAP, MOTION_QUERY } from '@/lib/gsap'

interface BeforeAfterProps {
    before: string
    after: string
    alt: string
    className?: string
    sizes?: string
    priority?: boolean
}

const clamp = (value: number): number => Math.min(100, Math.max(0, value))

export const BeforeAfter: FC<BeforeAfterProps> = ({
    before,
    after,
    alt,
    className,
    sizes = '(min-width: 1024px) 80vw, 100vw',
    priority = false
}): ReactNode => {
    const [position, setPosition] = useState(50)
    const containerRef = useRef<HTMLDivElement>(null)
    const draggingRef = useRef(false)
    const introRef = useRef<gsap.core.Tween | null>(null)

    useGSAP(() => {
        const element = containerRef.current

        if (!element) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            const proxy = { value: 50 }

            introRef.current = gsap.to(proxy, {
                keyframes: [
                    { value: 78, duration: 0.9, ease: 'power3.inOut' },
                    { value: 24, duration: 1.1, ease: 'power3.inOut' },
                    { value: 50, duration: 0.9, ease: 'power3.inOut' }
                ],
                scrollTrigger: { trigger: element, start: 'top 70%', once: true },
                onUpdate: () => setPosition(proxy.value)
            })
        })

        return () => media.revert()
    }, { scope: containerRef })

    const stopIntro = () => {
        introRef.current?.kill()
        introRef.current = null
    }

    const updateFromPointer = (clientX: number) => {
        const element = containerRef.current

        if (!element) return

        const rect = element.getBoundingClientRect()

        setPosition(clamp(((clientX - rect.left) / rect.width) * 100))
    }

    const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
        stopIntro()
        draggingRef.current = true
        event.currentTarget.setPointerCapture(event.pointerId)
        updateFromPointer(event.clientX)
    }

    const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
        if (draggingRef.current) updateFromPointer(event.clientX)
    }

    const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
        draggingRef.current = false

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId)
        }
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const steps: Record<string, number> = { ArrowLeft: -5, ArrowRight: 5, ArrowDown: -5, ArrowUp: 5, PageDown: -20, PageUp: 20 }

        if (event.key in steps) {
            event.preventDefault()
            stopIntro()
            setPosition(value => clamp(value + steps[event.key]))
        }
        else if (event.key === 'Home' || event.key === 'End') {
            event.preventDefault()
            stopIntro()
            setPosition(event.key === 'Home' ? 0 : 100)
        }
    }

    return (
        <div
            ref={containerRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            className={cn('relative cursor-ew-resize touch-pan-y select-none overflow-hidden bg-sand', className)}
        >
            <Image
                src={after}
                alt={`${alt}, after`}
                fill
                sizes={sizes}
                priority={priority}
                draggable={false}
                className='pointer-events-none object-cover'
            />

            <div className='absolute inset-0' style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}>
                <Image
                    src={before}
                    alt={`${alt}, before`}
                    fill
                    sizes={sizes}
                    priority={priority}
                    draggable={false}
                    className='pointer-events-none object-cover'
                />
            </div>

            <span className='pointer-events-none absolute left-4 top-4 rounded-full bg-ink/75 px-3 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] text-cream backdrop-blur-md md:left-6 md:top-6'>
                Before
            </span>

            <span className='pointer-events-none absolute right-4 top-4 rounded-full bg-cream/90 px-3 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink backdrop-blur-md md:right-6 md:top-6'>
                After
            </span>

            <div className='pointer-events-none absolute inset-y-0' style={{ left: `${position}%` }}>
                <div className='absolute inset-y-0 -left-px w-0.5 bg-cream shadow-[0_0_24px_rgba(0,0,0,0.35)]' />
            </div>

            <div
                role='slider'
                tabIndex={0}
                aria-label="Before and after slider. Use the arrow keys to move the divider."
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(position)}
                aria-valuetext={`${Math.round(position)}% before`}
                onKeyDown={handleKeyDown}
                className='absolute top-1/2 flex size-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-cream text-ink shadow-[0_12px_40px_-8px_rgba(0,0,0,0.5)] ring-4 ring-cream/30 transition-transform duration-300 hover:scale-105'
                style={{ left: `${position}%` }}
            >
                <ChevronsLeftRight className='size-5' aria-hidden='true' />
            </div>
        </div>
    )
}
